import React, { useState } from "react";
import {
  Badge, Box, Flex, HStack, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Text,
  VStack, useToast,
} from "@chakra-ui/react";
import TaskBoardCard from "./TaskBoardCard";
import TaskRow from "./TaskRow";
import { BOARD_COLUMNS, boardMove, viewFor } from "./boardModel";
import { withStatus } from "./tasksModel";
import { transitionWorkItem, transitionErrorMessage } from "../../services/workItemsService";
import { summaryKey } from "../../utils/workItemRefs";

// Messages → Tasks as a board: Open · In progress · In review · Completed.
// Drag a card to another column to move it (boardMove says whether you can,
// or why not); click it for everything the list row has.
// columns: boardColumns(...) for this user; items: every loaded item (the
// open card is looked up there, so it stays open after it moves).
export default function TaskBoard({
  items, columns, currentUserId, currentUserSchema, summaries, highlightKey, onItemChanged, onCopy,
}) {
  const [dragging, setDragging] = useState(null); // { item, targets: Set<columnId> }
  const [overColumn, setOverColumn] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [openKey, setOpenKey] = useState(null);
  const toast = useToast();
  const openItem = openKey ? (items || []).find((i) => i.key === openKey) : null;

  const startDrag = (e, item) => {
    try {
      e.dataTransfer?.setData("text/plain", item.key); // Firefox won't start a drag without data
      if (e.dataTransfer) e.dataTransfer.effectAllowed = "move";
    } catch {
      // Some browsers refuse; the drag still works.
    }
    const targets = new Set(BOARD_COLUMNS.map((c) => c.id).filter((id) => boardMove(item, id, currentUserId).action));
    setDragging({ item, targets });
  };
  const endDrag = () => {
    setDragging(null);
    setOverColumn(null);
  };

  const drop = async (columnId) => {
    const item = dragging?.item;
    endDrag();
    if (!item) return;
    const move = boardMove(item, columnId, currentUserId);
    if (!move.action) {
      if (move.reason) toast({ title: move.reason, status: "info", duration: 3000 });
      return;
    }
    setBusyKey(item.key);
    try {
      const { newStatus } = await transitionWorkItem(item, move.action, currentUserId, { to: move.to });
      onItemChanged(withStatus(item, newStatus));
    } catch (err) {
      toast({ title: "Couldn't move it", description: transitionErrorMessage(err), status: "error", duration: 5000 });
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <>
      <Flex gap={3} h="100%" minH={0} overflowX="auto" align="stretch" data-testid="task-board">
        {BOARD_COLUMNS.map((col) => {
          const cards = columns[col.id] || [];
          const target = !!dragging?.targets.has(col.id);
          return (
            <Flex
              key={col.id}
              direction="column"
              flex="1 0 220px"
              minW="220px"
              minH={0}
              bg="gray.50"
              _dark={{ bg: "gray.800" }}
              borderRadius="md"
              borderWidth="2px"
              borderStyle={target ? "dashed" : "solid"}
              borderColor={target ? (overColumn === col.id ? "blue.500" : "blue.200") : "transparent"}
              data-testid={`board-column-${col.id}`}
              onDragOver={(e) => {
                if (!dragging) return; // not one of our cards (e.g. a file): leave it to the browser
                e.preventDefault();
                if (overColumn !== col.id) setOverColumn(col.id);
              }}
              onDragLeave={() => setOverColumn((c) => (c === col.id ? null : c))}
              onDrop={(e) => {
                if (!dragging) return;
                e.preventDefault();
                drop(col.id);
              }}
            >
              <HStack px={3} py={2} spacing={2}>
                <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color="gray.600" letterSpacing="wide">
                  {col.label}
                </Text>
                <Badge borderRadius="full" data-testid={`board-count-${col.id}`}>{cards.length}</Badge>
              </HStack>
              <VStack align="stretch" spacing={2} px={2} pb={2} overflowY="auto" flex="1" minH="60px">
                {cards.map((item) => (
                  <TaskBoardCard
                    key={item.key}
                    item={item}
                    currentUserId={currentUserId}
                    summary={summaries?.[summaryKey(item)]}
                    highlighted={item.key === highlightKey}
                    busy={busyKey === item.key}
                    onOpen={() => setOpenKey(item.key)}
                    onDragStart={startDrag}
                    onDragEnd={endDrag}
                  />
                ))}
                {cards.length === 0 && (
                  <Text fontSize="xs" color="gray.400" textAlign="center" py={4}>Nothing here</Text>
                )}
              </VStack>
            </Flex>
          );
        })}
      </Flex>
      {openItem && (
        <Modal isOpen onClose={() => setOpenKey(null)} size="2xl" scrollBehavior="inside" blockScrollOnMount={false}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader pr={10} fontSize="md">{openItem.kind === "order" ? "Order" : "Task"}</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={4}>
              <Box>
                <TaskRow
                  item={openItem}
                  view={viewFor(openItem, currentUserId)}
                  currentUserId={currentUserId}
                  currentUserSchema={currentUserSchema}
                  summary={summaries?.[summaryKey(openItem)]}
                  onItemChanged={onItemChanged}
                  onCopy={onCopy}
                />
              </Box>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </>
  );
}
